import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

// Translation data
const translations = {
  en: {
    // Navigation
    home: "Home",
    documents: "Documents",
    suppliers: "Suppliers",
    warehouse: "Warehouse",
    company: "Company",
    llmSettings: "LLM Settings",
    profile: "Profile",
    logout: "Logout",

    // Common
    search: "Search",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    close: "Close",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    view: "View",
    details: "Details",
    openFullView: "Open full view",
    sidenavColors: "Sidenav Colors",
    sidenavType: "Sidenav Type",
    chooseSidenavType: "Choose between different sidenav types.",
    dark: "Dark",
    transparent: "Transparent",
    white: "White",
    navbarFixed: "Navbar Fixed",
    lightDark: "Light / Dark",
    select: "Select",
    selected: "Selected",
    notSelected: "Not Selected",
    primary: "Primary",
    remove: "Remove",
    setPrimary: "Set as Primary",
    total: "Total",
    discount: "Discount",
    settings: "Settings",
    customizeApplicationPreferences: "Customize application preferences",
    appearance: "Appearance",
    quickLinks: "Quick Links",
    changePassword: "Change Password",
    settingsSavedAutomatically: "Settings are saved automatically",
    settingsSavedAutomaticallyDescription:
      "Your interface preferences are stored locally in this browser and applied automatically next time.",
    themeModeDescription: "Switch between light and dark interface.",
    navbarFixedDescription: "Keep the top navigation visible while scrolling.",
    // Snackbars
    roleUpdated: "Role updated",
    userActivated: "User activated",
    userDeactivated: "User deactivated",
    assignedToCompany: "Assigned to company",
    removedFromCompany: "Removed from company",
    userCreated: "User created",
    passwordUpdated: "Password updated",
    cannotDeactivateSelf: "You cannot deactivate your own account.",
    cannotDeactivateSuperuser: "You cannot deactivate a superuser.",
    cannotDeactivatePrimaryAdmin: "You cannot deactivate the primary admin account.",

    // Document Page
    documentsManagement: "Documents Management",
    totalDocuments: "Total Documents:",
    searchDocuments: "Search documents...",
    uploadDocuments: "Upload Documents",
    uploadFromGallery: "Upload from Gallery",
    takePhoto: "Take Photo",
    uploading: "Uploading",
    uploadingFiles: "Uploading {count} files...",
    files: "files",
    failedFiles: "Failed files",
    uploadCompletedWithIssues: "Upload completed with issues. Failed: {count}",
    uploadCancelled: "Upload cancelled",
    badRequest: "Bad request (400).",
    recognize: "Recognize",
    recognizeUnrecognized: "Recognize Unrecognized",
    recognizeHeaders: "Recognize Headers",
    deleteSelected: "Delete Selected",
    selectedDocuments: "Selected documents",
    exportXml: "Export XML",
    export: "Export",
    exportExcel: "Export Excel",
    exportRodaTxt: "Export to RODA (TXT)",
    exportingXml: "Exporting {count} documents to XML...",
    exportingExcel: "Exporting {count} documents to Excel...",
    showingDocumentsCount: "Showing {shown} of {total} documents",
    activeFiltersCount: "Active: {count}",
    generatePdfReport: "Generate PDF Report",
    applyFilters: "Apply at least one filter to load documents",
    noDocumentsFound: "No documents found for the selected filters",
    exportWithMatches: "Export with Matches",

    // Filters
    manageColumns: "Manage Columns",
    suppliers: "Suppliers",
    excludeSuppliers: "Exclude Suppliers",
    dateType: "Date Type",
    invoiceDate: "Invoice Date",
    uploadDate: "Upload Date",
    startDate: "Start Date",
    endDate: "End Date",
    xmlType: "XML Type",
    resetFilters: "Reset Filters",
    filters: "Filters",
    searchAndFilters: "Search & Filters",
    searchSuppliers: "Search suppliers...",
    all: "All",
    apply: "Apply",

    // Document Details
    documentDetails: "Document Details",
    supplier: "Supplier",
    invoice: "Invoice",
    date: "Date",
    supplierInformation: "Supplier Information",
    receiverInformation: "Receiver Information",
    supplierName: "Supplier Name",
    supplierTaxId: "Supplier Tax ID",
    supplierIban: "Supplier IBAN",
    supplierAddress: "Supplier Address",
    supplierRegistration: "Supplier Registration",
    receiverName: "Receiver Name",
    receiverTaxId: "Receiver Tax ID",
    receiverRegistration: "Receiver Registration",
    receiverAddress: "Receiver Address",
    receiverIban: "Receiver IBAN",
    invoiceId: "Invoice ID",
    totalAmount: "Total Amount",
    totalTaxAmount: "Total Tax Amount",
    netAmount: "Net Amount",
    currency: "Currency",
    dueDate: "Due Date",
    vatPercentage: "VAT Percentage",
    saveChanges: "Save Changes",
    notes: "Notes",
    recognizedText: "Recognized Text",
    formattedText: "Formatted Text",
    extractedTables: "Extracted Tables",
    table: "Table",
    documentPreview: "Document Preview",
    noRecognizedTextAvailable: "No recognized text available",
    noFormattedTextAvailable: "No formatted text available",
    saveNotes: "Save Notes",
    tokenUsage: "Token Usage",
    promptTokens: "Prompt Tokens",
    completionTokens: "Completion Tokens",
    totalTokens: "Total Tokens",
    unknownUploader: "Unknown uploader",
    relatedFieldValidation: "Related field validation",
    validationTotalsMatch: "Totals are consistent",
    validationTotalsMismatch: "Totals do not match",
    validationDatesMatch: "Invoice date and due date are consistent",
    validationDatesMismatch: "Due date is earlier than invoice date",
    noRelatedValidationRules: "No related validation rules for this field",

    // Line Items
    lineItems: "Line Items",
    name: "Name",
    qty: "Qty",
    price: "Price",
    sku: "SKU",
    description: "Description",
    quantity: "Quantity",
    unitPrice: "Unit Price",
    unitOfMeasure: "Unit of Measure",
    totalQuantity: "Total Quantity",
    addLineItem: "Add Line Item",

    // PDF Reports
    selectReportType: "Select Report Type",
    purchaseDocumentsReport: "Purchase Documents Report",
    salesDocumentsReport: "Sales Documents Report",
    generate: "Generate",
    generating: "Generating...",

    // Recognition Options
    recognitionOptions: "Recognition Options",
    useCompanyCode: "Use company code (default)",
    useLlmPrompt: "Use value from LLM prompt",
    proceed: "Proceed",

    // Additional Recognition Options
    companyCodeOption: "Company Code Option",
    useCompanyCodeFromSettings: "Use company code from settings",
    extractCompanyCodeFromDocument: "Extract company code from document",
    recognitionMethod: "Recognition Method",
    traditionalMethod: "Traditional method",
    markdownMethod: "Markdown method (Enhanced)",
    markdownMethodDescription:
      "Uses advanced markdown formatting for better document structure recognition",
    selectRecognitionOptions: "Select recognition options before processing documents",
    startRecognition: "Start Recognition",

    // PDF Dialog
    selectPdfType: "Select PDF Type",
    selectPdfTypeDescription: "Choose the type of documents to include in the PDF report",
    purchaseInvoices: "Purchase Invoices",
    salesInvoices: "Sales Invoices",

    // Messages
    selectCompanyFirst: "Please select a company first",
    noDocumentsSelected: "No documents selected",
    documentsDeletedSuccessfully: "Documents deleted successfully",
    documentsSavedSuccessfully: "Documents saved successfully",
    recognitionCompleted: "Recognition completed successfully",
    pdfGeneratedSuccessfully: "PDF report generated successfully",
    xmlExportedSuccessfully: "XML exported successfully",
    exportToAgnum: "Export to Agnum",

    // Company Management
    companyManagement: "Company Management",
    companyName: "Company Name",
    companyCode: "Company Code",
    addCompany: "Add Company",
    editCompany: "Edit Company",
    deleteCompany: "Delete Company",
    addNewCompany: "Add New Company",
    status: "Status",
    selectCompany: "Select Company",

    // Supplier Management
    supplierManagement: "Supplier Management",
    addSupplier: "Add Supplier",
    editSupplier: "Edit Supplier",
    importSuppliers: "Import Suppliers",
    registration: "Registration",
    primaryWarehouse: "Primary Warehouse",
    primaryGroup: "Primary Group",
    pvmCode: "PVM Code",
    warehouseCode: "Warehouse Code",
    groupName: "Group Name",
    xmlType: "XML Type",
    vatCode: "VAT Code",
    deletePrimary: "Delete / Primary",

    // Warehouse Management
    warehouseManagement: "Warehouse Management",
    warehouseCode: "Warehouse Code",
    warehouseName: "Warehouse Name",
    addWarehouse: "Add Warehouse",
    editWarehouse: "Edit Warehouse",
    group: "Group",
    itemCode: "Item Code",
    name: "Name",
    uploadItems: "Upload Items",

    // LLM Settings
    llmSettingsManagement: "LLM Settings Management",
    modelVersion: "Model Version",
    apiKey: "API Key",
    temperature: "Temperature",
    maxTokens: "Max Tokens",
    "LLM Settings": "LLM Settings",
    "API Keys": "API Keys",
    "Azure Endpoint": "Azure Endpoint",
    "Azure API Key": "Azure API Key",
    "Content Understanding Endpoint": "Content Understanding Endpoint",
    "Content Understanding API Key": "Content Understanding API Key",
    "Content Understanding Analyzer ID": "Content Understanding Analyzer ID",
    "Content Understanding API Version": "Content Understanding API Version",
    "Foundry resource endpoint for Content Understanding":
      "Foundry resource endpoint for Content Understanding",
    "Use prebuilt-invoice or your custom analyzer ID":
      "Use prebuilt-invoice or your custom analyzer ID",
    "Anthropic API Key": "Anthropic API Key",
    "OpenAI API Key": "OpenAI API Key",
    "Google API Key": "Google API Key",
    "Model Settings": "Model Settings",
    "Default Model Provider": "Default Model Provider",
    "Azure Content Understanding": "Azure Content Understanding",
    "OpenAI Model Version": "OpenAI Model Version",
    "Claude Model Version": "Claude Model Version",
    "Gemini Model Version": "Gemini Model Version",
    "OpenAI Vision Settings": "OpenAI Vision Settings",
    "Use OpenAI Vision API": "Use OpenAI Vision API",
    "OpenAI Vision Custom Prompt": "OpenAI Vision Custom Prompt",
    "Processing Instructions": "Processing Instructions",
    "LLM Instructions": "LLM Instructions",
    "Additional Information": "Additional Information",
    "Save Settings": "Save Settings",
    "Settings saved successfully!": "Settings saved successfully!",
    "Import from Another Company": "Import from Another Company",
    "Import LLM Settings": "Import LLM Settings",
    "Select a company to import LLM settings from. This will copy all API keys, model configurations, and prompts from the selected company.":
      "Select a company to import LLM settings from. This will copy all API keys, model configurations, and prompts from the selected company.",
    "No other companies with LLM settings found": "No other companies with LLM settings found",
    "Source Company": "Source Company",
    Import: "Import",
    "Importing...": "Importing...",
    "shared across all companies": "shared across all companies",
    "Directly process documents using OpenAI Vision instead of Azure Form Recognizer":
      "Directly process documents using OpenAI Vision instead of Azure Form Recognizer",
    "No (Use Azure Form Recognizer)": "No (Use Azure Form Recognizer)",
    "Yes (Use OpenAI Vision API)": "Yes (Use OpenAI Vision API)",
    "Custom prompt for OpenAI Vision processing. Leave empty to use default prompt.":
      "Custom prompt for OpenAI Vision processing. Leave empty to use default prompt.",

    // Dual-Pass LLM Settings
    "Dual-Pass LLM Settings": "Dual-Pass LLM Settings",
    "Enhanced accuracy with two-stage processing": "Enhanced accuracy with two-stage processing",
    "Enable Dual-Pass Processing": "Enable Dual-Pass Processing",
    "No (Use Single Model)": "No (Use Single Model)",
    "Yes (Use Dual-Pass)": "Yes (Use Dual-Pass)",
    "Primary Model (Pass 1)": "Primary Model (Pass 1)",
    "Primary Model Version": "Primary Model Version",
    "Validation Model (Pass 2)": "Validation Model (Pass 2)",
    "Validation Model Version": "Validation Model Version",
    "Validation Custom Prompt": "Validation Custom Prompt",
    "Custom prompt for validation pass. Leave empty to use default validation prompt.":
      "Custom prompt for validation pass. Leave empty to use default validation prompt.",

    // Authentication
    signIn: "Sign In",
    signUp: "Sign Up",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    confirmPasswordLabel: "Confirm password",
    passwordsMustMatch: "Passwords must match",
    firstName: "First Name",
    lastName: "Last Name",
    forgotPassword: "Forgot Password?",
    resetPassword: "Reset Password",

    // Table Headers
    title: "Title",
    uploadedBy: "Uploaded By",
    createdAt: "Created At",
    recognized: "Recognized",
    rec: "Rec",
    valid: "Valid",
    actions: "Actions",
    showAll: "Show all",
    assign: "Assign",
    invoiceDate: "Invoice Date",
    taxAmount: "Tax Amount",
    supplierName: "Supplier Name",
    supplierRegistration: "Supplier Registration",
    receiverName: "Receiver Name",
    receiverRegistration: "Receiver Registration",
    description: "Description",

    // Pagination
    docsPerPage: "Docs per page",
    page: "Page",
    of: "of",
    perPage: "Per Page",

    // Language
    language: "Language",
    english: "English",
    lithuanian: "Lithuanian",

    // Dialogs and Modals
    deleteConfirmation: "Delete Confirmation",
    areYouSure: "Are you sure you want to delete",
    thisActionCannotBeUndone: "This action cannot be undone.",
    refreshList: "Refresh list",
    backToDocuments: "Back to Documents",

    // XML Export Settings
    xmlExportSettings: "XML Export Settings",
    selectXmlExportSettings: "Select XML export options and markers",
    exportType: "Export type",
    purchases: "Purchases",
    salesInvoices: "Sales",
    summary: "Summary",
    recalcVatTitle: "Recalculate VAT (21%) if document VAT is 0",
    recalcVatDescription:
      "When enabled, export will automatically calculate 21% VAT for documents where VAT is 0.",
    includePvmInTotal: "Include VAT in total amount (M4)",
    spacyModelSize: "spaCy Model Size",
    mediumModel: "Medium Model (md) - Faster",
    largeModel: "Large Model (lg) - More Accurate",
    spacyModelDescription:
      "Medium model is faster but less accurate. Large model is slower but more accurate for text matching.",
    pvmCodeOverride: "PVM Code Override",
    selectPvmCode: "Select PVM Code",
    useSupplierPvmCode: "Use supplier's PVM code (default)",
    pvmCodeOverrideDescription:
      "If selected, this PVM code will override the supplier's PVM code for all exported documents.",

    // Additional missing translations
    exportWithMatches: "Export with Matches",
    importSuppliers: "Import Suppliers",
    savedVariants: "Saved Variants",
    // Search bar
    searchIn: "Search in",
    allFields: "All fields",
    documentTitle: "Document Title",
    exactMatch: "Exact match",
    searchingIn: "Searching in",
    contains: "contains",
    zoomOut: "Zoom out",
    zoomIn: "Zoom in",
    previousPage: "Previous page",
    nextPage: "Next page",
    previousDocument: "Previous document",
    nextDocument: "Next document",
    recognizeDocument: "Recognize document",
    showFieldMarkers: "Show field markers",
    hideFieldMarkers: "Hide field markers",
    fieldMarkers: "Field markers",
    noFieldMarkersOnPage: "No field markers on this page",
    fieldMarkersHidden: "Field markers are hidden",
    deleteDocument: "Delete document",
    downloadDocument: "Download document",
    printDocument: "Print document",
    documentFileUrlMissing: "Document file URL is missing.",

    // User Management
    userManagement: "User Management",
    addUser: "Add user",
    filterByCompany: "Filter by company",
    assignToCompany: "Assign to company",
    setPassword: "Set password",
    newPassword: "New password",
    role: "Role",
    active: "Active",
    inactive: "Inactive",
    admin: "Admin",
    user: "User",
    guest: "Guest",
    noAccess: "No access",

    // Warehouse & Suppliers additions
    addItem: "Add Item",
    addWarehouseItem: "Add Warehouse Item",
    editWarehouseItem: "Edit Warehouse Item",
    itemName: "Item Name",
    itemGroup: "Item Group",
    confirmDeleteItem: "Are you sure you want to delete this item?",
    warehouseItemDeletedSuccessfully: "Warehouse item deleted successfully.",
    warehouseItemsUploadedSuccessfully: "Warehouse items uploaded successfully!",
    supplierListRefreshed: "Supplier list refreshed!",
    warehouseListRefreshed: "Warehouse list refreshed!",
    suppliersImportedSuccessfully: "Suppliers imported successfully!",
    importCompleted: "Import completed.",
    createdSuppliers: "Created suppliers",
    createdCount: "Created: {count}",
    noCreatedSuppliers: "No new suppliers were created.",
    filesUploadedSuccessfully: "Files uploaded successfully",
    selectAtLeastOneFile: "Please select at least one file to upload.",
    failedToUploadWarehouseItems: "Failed to upload warehouse items.",
    unitPriceOptional: "Optional. Set a unit price for the report (leave empty to skip).",
    deleteSelectedConfirm:
      "Are you sure you want to delete {count} selected document(s)? This action cannot be undone.",
    perPage: "Per Page",
    errorFetchingWarehouseItems: "Error fetching warehouse items.",
    errorDeletingWarehouseItem: "Error deleting warehouse item.",
    warehouseItemUpdatedSuccessfully: "Item updated successfully!",
    warehouseItemCreatedSuccessfully: "Item created successfully!",
    unknownErrorOccurred: "An unknown error occurred",
    failedToSaveItem: "Failed to save item.",
    // Document viewer actions
    confirmDeleteDocument: "Are you sure you want to delete this document?",
    documentDeletedSuccessfully: "Document deleted successfully",
    errorDeletingDocument: "Error deleting document",
    notesSavedSuccessfully: "Notes saved successfully",
    errorSavingNotes: "Error saving notes",
    informationSavedSuccessfully: "Information saved successfully",
    errorSavingInformation: "Error saving information",
    documentUpdatedSuccessfully: "Document updated successfully",
    failedToUpdateDocument: "Failed to update document",
    lineItemsSavedSuccessfully: "Line items saved successfully",
    errorSavingLineItems: "Error saving line items",
    documentRecognizedSuccessfully: "Document successfully recognized",
    errorRecognizingDocument: "Error recognizing document",
    gatewayTimeoutSuffix: ": server timeout (504)",
    filesUploadedSuccessfully: "Successfully uploaded documents.",
    failedToUploadDocuments: "Failed to upload documents",
    errorUploadingFiles: "Error uploading files",
    // Server summary patterns
    uploadSummary:
      "Successfully uploaded {uploaded} documents. Failed to upload {failed} documents.",
    documentsDeletedCount: "{count} documents have been deleted.",
    // Recognition progress
    processingDocumentProgress: "Processing document {i}/{n} (ID {id})...",
    processingBatchStart: "Starting recognition for {n} documents...",
    recognitionSummary: "Recognition finished. Success: {ok}, Failed: {fail}.",
    recognitionFailedFor: "Recognition failed for document {id}.",
    notRecognized: "Not recognized",
    unknownSupplier: "Unknown supplier",
    unitPrice: "Unit Price",
    processing: "Processing...",
    cancelRecognition: "Cancel recognition",
    cancelling: "Cancelling...",
    recognitionInProgress: "Recognition in progress",
    recognitionCancelled: "Recognition cancelled",
    recognitionFinished: "Recognition finished",
    documentsProcessed: "Documents processed",
    recognitionWasCancelled: "Batch was cancelled.",
    recognitionAllSuccess: "All {ok} documents recognized successfully!",
    recognitionCancelRequested: "Recognition cancel requested",
    recognitionCancelFailed: "Failed to request cancel",
    // XML export background
    xmlExportInProgress: "XML export in progress",
    xmlExportFailed: "XML export failed",
    xmlExportReady: "XML ready to download",
    downloadXml: "Download XML",
    // Generic notifications
    savedSuccessfully: "Saved successfully",
    updatedSuccessfully: "Updated successfully",
    errorOccurred: "An error occurred",
    // Document Type Labels
    documentType: "Document Type",
    purchaseDocument: "Purchase Document",
    salesDocument: "Sales Document",
    returnDocument: "Return / Credit Document",
    otherDocument: "Other Document",
    unknownDocumentType: "Unknown Document Type",

    // Product Matching LLM Settings
    "Product Matching LLM Settings": "Product Matching LLM Settings",
    "Product Matching Model": "Product Matching Model",
    "Product Matching Model Version": "Product Matching Model Version",
    matcherType: "Matching Method",
    spacy: "spaCy / Fuzzy",
    llm: "LLM (AI)",

    // Recognition status filter
    recognitionStatus: "Recognition Status",
    unrecognized: "Unrecognized",

    // User management
    selectAllCompanies: "Select all companies",
  },
  lt: {
    // Configurator
    settings: "Nustatymai",
    customizeApplicationPreferences: "Pritaikykite programos nustatymus",
    appearance: "Išvaizda",
    quickLinks: "Greitos nuorodos",
    changePassword: "Keisti slaptažodį",
    settingsSavedAutomatically: "Nustatymai išsaugomi automatiškai",
    settingsSavedAutomaticallyDescription:
      "Jūsų sąsajos nustatymai išsaugomi šioje naršyklėje ir automatiškai pritaikomi kitą kartą.",
    themeModeDescription: "Perjunkite tarp šviesios ir tamsios sąsajos.",
    navbarFixedDescription: "Palikite viršutinę juostą matomą slenkant puslapį.",
    sidenavColors: "Šoninio meniu spalvos",
    sidenavType: "Šoninio meniu tipas",
    chooseSidenavType: "Pasirinkite skirtingus šoninio meniu tipus.",
    dark: "Tamsus",
    transparent: "Permatomas",
    white: "Baltas",
    navbarFixed: "Fiksuota navigacija",
    lightDark: "Šviesus / Tamsus",
    // Navigation
    home: "Pagrindinis",
    documents: "Dokumentai",
    suppliers: "Tiekėjai",
    warehouse: "Sandėlis",
    company: "Įmonė",
    llmSettings: "LLM Nustatymai",
    profile: "Profilis",
    logout: "Atsijungti",

    // Common
    search: "Ieškoti",
    loading: "Kraunama...",
    save: "Išsaugoti",
    cancel: "Atšaukti",
    delete: "Ištrinti",
    edit: "Redaguoti",
    add: "Pridėti",
    close: "Uždaryti",
    confirm: "Patvirtinti",
    yes: "Taip",
    no: "Ne",
    view: "Peržiūrėti",
    details: "Detalės",
    openFullView: "Atidaryti pilną peržiūrą",
    select: "Pasirinkti",
    selected: "Pasirinkta",
    notSelected: "Nepasirinkta",
    primary: "Pagrindinis",
    remove: "Pašalinti",
    setPrimary: "Nustatyti kaip pagrindinį",
    total: "Bendra",
    discount: "Nuolaida",
    // Snackbars
    roleUpdated: "Rolė atnaujinta",
    userActivated: "Vartotojas aktyvuotas",
    userDeactivated: "Vartotojas deaktyvuotas",
    assignedToCompany: "Priskirta įmonei",
    removedFromCompany: "Pašalinta iš įmonės",
    userCreated: "Vartotojas sukurtas",
    passwordUpdated: "Slaptažodis atnaujintas",
    cannotDeactivateSelf: "Negalite deaktyvuoti savo paskyros.",
    cannotDeactivateSuperuser: "Negalite deaktyvuoti supervartotojo.",
    cannotDeactivatePrimaryAdmin: "Negalite deaktyvuoti pagrindinės administratoriaus paskyros.",

    // Document Page
    documentsManagement: "Dokumentų valdymas",
    totalDocuments: "Rasta dokumentų:",
    searchDocuments: "Ieškoti dokumentų...",
    uploadDocuments: "Įkelti dokumentus",
    uploadFromGallery: "Įkelti iš galerijos",
    takePhoto: "Fotografuoti",
    uploading: "Įkeliama",
    uploadingFiles: "Įkeliami {count} failai...",
    files: "failai",
    failedFiles: "Nepavykę failai",
    uploadCompletedWithIssues: "Įkėlimas baigtas su problemomis. Nepavyko: {count}",
    uploadCancelled: "Įkėlimas atšauktas",
    badRequest: "Neteisinga užklausa (400).",
    recognize: "Atpažinti",
    recognizeUnrecognized: "Atpažinti neatpažintus",
    recognizeHeaders: "Atpažinti antraštes",
    deleteSelected: "Ištrinti pasirinktus",
    selectedDocuments: "Pasirinkti dokumentai",
    exportXml: "Eksportuoti XML",
    export: "Eksportuoti",
    exportExcel: "Eksportuoti Excel",
    exportRodaTxt: "Eksportuoti į RODA (TXT)",
    exportingXml: "Eksportuojami {count} dokumentai į XML...",
    exportingExcel: "Eksportuojami {count} dokumentai į Excel...",
    showingDocumentsCount: "Rodoma {shown} iš {total} dokumentų",
    activeFiltersCount: "Aktyvūs: {count}",
    generatePdfReport: "Generuoti PDF ataskaitą",
    applyFilters: "Pritaikykite bent vieną filtrą dokumentų įkėlimui",
    noDocumentsFound: "Nerasta dokumentų pagal pasirinktus filtrus",
    exportWithMatches: "Eksportuoti su atitikimais",

    // Filters
    manageColumns: "Stulpelių valdymas",
    suppliers: "Tiekėjai",
    excludeSuppliers: "Išskirti tiekėjus",
    dateType: "Datos tipas",
    invoiceDate: "Sąskaitos data",
    uploadDate: "Įkėlimo data",
    startDate: "Pradžios data",
    endDate: "Pabaigos data",
    xmlType: "XML tipas",
    resetFilters: "Atstatyti filtrus",
    filters: "Filtrai",
    searchAndFilters: "Paieška ir filtrai",
    searchSuppliers: "Ieškoti tiekėjų...",
    all: "Visi",
    apply: "Pritaikyti",

    // Document Details
    documentDetails: "Dokumento detalės",
    supplier: "Tiekėjas",
    invoice: "Sąskaita",
    date: "Data",
    supplierInformation: "Tiekėjo informacija",
    receiverInformation: "Gavėjo informacija",
    supplierName: "Tiekėjo pavadinimas",
    supplierTaxId: "Tiekėjo PVM kodas",
    supplierIban: "Tiekėjo IBAN",
    supplierAddress: "Tiekėjo adresas",
    supplierRegistration: "Tiekėjo įmonės kodas",
    receiverName: "Gavėjo pavadinimas",
    receiverTaxId: "Gavėjo PVM kodas",
    receiverRegistration: "Gavėjo įmonės kodas",
    receiverAddress: "Gavėjo adresas",
    receiverIban: "Gavėjo IBAN",
    invoiceId: "Sąskaitos numeris",
    totalAmount: "Suma su PVM",
    totalTaxAmount: "PVM suma",
    netAmount: "Suma be PVM",
    currency: "Valiuta",
    dueDate: "Mokėjimo terminas",
    vatPercentage: "PVM procentas",
    saveChanges: "Išsaugoti pakeitimus",
    notes: "Pastabos",
    recognizedText: "Atpažintas tekstas",
    formattedText: "Formatuotas tekstas",
    extractedTables: "Ištrauktos lentelės",
    table: "Lentelė",
    documentPreview: "Dokumento peržiūra",
    noRecognizedTextAvailable: "Nėra atpažinto teksto",
    noFormattedTextAvailable: "Nėra formatuoto teksto",
    saveNotes: "Išsaugoti pastabas",
    tokenUsage: "Žetonų naudojimas",
    promptTokens: "Prompt žetonai",
    completionTokens: "Completion žetonai",
    totalTokens: "Iš viso žetonų",
    unknownUploader: "Nežinomas įkėlėjas",
    relatedFieldValidation: "Susijusių laukų patikra",
    validationTotalsMatch: "Sumos tarpusavyje sutampa",
    validationTotalsMismatch: "Sumos tarpusavyje nesutampa",
    validationDatesMatch: "Sąskaitos data ir mokėjimo terminas suderinti",
    validationDatesMismatch: "Mokėjimo terminas yra ankstesnis už sąskaitos datą",
    noRelatedValidationRules: "Šiam laukui nėra susijusių patikros taisyklių",

    // Line Items
    lineItems: "Eilutės",
    name: "Pavadinimas",
    qty: "Kiekis",
    price: "Kaina",
    sku: "Kodas",
    description: "Aprašymas",
    quantity: "Kiekis",
    unitPrice: "Vnt. kaina",
    unitOfMeasure: "Mat. vnt.",
    totalQuantity: "Bendras kiekis",
    addLineItem: "Pridėti eilutę",

    // PDF Reports
    selectReportType: "Pasirinkite ataskaitos tipą",
    purchaseDocumentsReport: "Pirkimo dokumentų ataskaita",
    salesDocumentsReport: "Pardavimo dokumentų ataskaita",
    generate: "Generuoti",
    generating: "Generuojama...",

    // Recognition Options
    recognitionOptions: "Atpažinimo parinktys",
    useCompanyCode: "Naudoti įmonės kodą (numatytasis)",
    useLlmPrompt: "Naudoti reikšmę iš LLM užklausos",
    proceed: "Tęsti",

    // Additional Recognition Options
    companyCodeOption: "Įmonės kodas",
    useCompanyCodeFromSettings: "Naudoti įmonės kodą iš nustatymų",
    extractCompanyCodeFromDocument: "Ištraukti įmonės kodą iš dokumento",
    recognitionMethod: "Atpažinimo metodas",
    traditionalMethod: "Tradicinis metodas",
    markdownMethod: "Markdown metodas (Pagerintas)",
    markdownMethodDescription:
      "Naudoja pagerintą markdown formatavimą geresniam dokumento struktūros atpažinimui",
    selectRecognitionOptions: "Pasirinkite atpažinimo parinktis prieš apdorojant dokumentus",
    startRecognition: "Pradėti atpažinimą",

    // PDF Dialog
    selectPdfType: "Pasirinkite PDF tipą",
    selectPdfTypeDescription: "Pasirinkite dokumentų tipą, kurį norite įtraukti į PDF ataskaitą",
    purchaseInvoices: "Pirkimo sąskaitos",
    salesInvoices: "Pardavimo sąskaitos",

    // Messages
    selectCompanyFirst: "Pirmiausia pasirinkite įmonę",
    noDocumentsSelected: "Nepasirinkta dokumentų",
    documentsDeletedSuccessfully: "Dokumentai sėkmingai ištrinti",
    documentsSavedSuccessfully: "Dokumentai sėkmingai išsaugoti",
    recognitionCompleted: "Atpažinimas sėkmingai baigtas",
    pdfGeneratedSuccessfully: "PDF ataskaita sėkmingai sugeneruota",
    xmlExportedSuccessfully: "XML sėkmingai eksportuotas",
    exportToAgnum: "Eksportas į Agnum",

    // Company Management
    companyManagement: "Įmonių valdymas",
    companyName: "Įmonės pavadinimas",
    companyCode: "Įmonės kodas",
    addCompany: "Pridėti įmonę",
    editCompany: "Redaguoti įmonę",
    deleteCompany: "Ištrinti įmonę",
    addNewCompany: "Pridėti naują įmonę",
    status: "Būsena",
    selectCompany: "Pasirinkti įmonę",

    // Supplier Management
    supplierManagement: "Tiekėjų valdymas",
    addSupplier: "Pridėti tiekėją",
    editSupplier: "Redaguoti tiekėją",
    importSuppliers: "Importuoti tiekėjus",
    registration: "Registracija",
    primaryWarehouse: "Pagrindinis sandėlis",
    primaryGroup: "Pagrindinė grupė",
    pvmCode: "PVM kodas",
    warehouseCode: "Sandėlio kodas",
    groupName: "Grupės pavadinimas",
    xmlType: "XML tipas",
    vatCode: "PVM kodas",
    deletePrimary: "Ištrinti / Pagrindinis",

    // Warehouse Management
    warehouseManagement: "Sandėlių valdymas",
    warehouseCode: "Sandėlio kodas",
    warehouseName: "Sandėlio pavadinimas",
    addWarehouse: "Pridėti sandėlį",
    editWarehouse: "Redaguoti sandėlį",
    group: "Grupė",
    itemCode: "Prekės kodas",
    name: "Pavadinimas",
    uploadItems: "Įkelti prekes",

    // LLM Settings
    llmSettingsManagement: "LLM nustatymų valdymas",
    modelVersion: "Modelio versija",
    apiKey: "API raktas",
    temperature: "Temperatūra",
    maxTokens: "Maksimalus žetonų skaičius",
    "LLM Settings": "LLM Nustatymai",
    "API Keys": "API raktų",
    "Azure Endpoint": "Azure Endpoint",
    "Azure API Key": "Azure API raktas",
    "Content Understanding Endpoint": "Content Understanding Endpoint",
    "Content Understanding API Key": "Content Understanding API raktas",
    "Content Understanding Analyzer ID": "Content Understanding analizatoriaus ID",
    "Content Understanding API Version": "Content Understanding API versija",
    "Foundry resource endpoint for Content Understanding":
      "Foundry resurso adresas Content Understanding",
    "Use prebuilt-invoice or your custom analyzer ID":
      "Naudokite prebuilt-invoice arba savo analizatoriaus ID",
    "Anthropic API Key": "Anthropic API raktas",
    "OpenAI API Key": "OpenAI API raktas",
    "Google API Key": "Google API raktas",
    "Model Settings": "Modelio nustatymai",
    "Default Model Provider": "Numatytasis modelio tiekėjas",
    "Azure Content Understanding": "Azure Content Understanding",
    "OpenAI Model Version": "OpenAI modelio versija",
    "Claude Model Version": "Claude modelio versija",
    "Gemini Model Version": "Gemini modelio versija",
    "OpenAI Vision Settings": "OpenAI vaizdo nustatymai",
    "Use OpenAI Vision API": "Naudoti OpenAI vaizdo API",
    "OpenAI Vision Custom Prompt": "OpenAI vaizdo kustomas užklausas",
    "Processing Instructions": "Apdorojimo instrukcijos",
    "LLM Instructions": "LLM instrukcijos",
    "Additional Information": "Papildoma informacija",
    "Save Settings": "Išsaugoti nustatymus",
    "Settings saved successfully!": "Nustatymai sėkmingai išsaugoti!",
    "Import from Another Company": "Importuoti iš kitos įmonės",
    "Import LLM Settings": "Importuoti LLM nustatymus",
    "Select a company to import LLM settings from. This will copy all API keys, model configurations, and prompts from the selected company.":
      "Pasirinkite įmonę, iš kurios importuoti LLM nustatymus. Tai nukopijuos visus API raktus, modelių konfigūracijas ir užklausas iš pasirinktos įmonės.",
    "No other companies with LLM settings found": "Nerastos kitos įmonės su LLM nustatymais",
    "Source Company": "Šaltinio įmonė",
    Import: "Importuoti",
    "Importing...": "Importuojama...",
    "shared across all companies": "dalinamas visoms įmonėms",
    "Directly process documents using OpenAI Vision instead of Azure Form Recognizer":
      "Direktinai apdoroti dokumentus naudojant OpenAI vaizdą, o ne Azure Form Recognizer",
    "No (Use Azure Form Recognizer)": "Ne (Naudoti Azure Form Recognizer)",
    "Yes (Use OpenAI Vision API)": "Taip (Naudoti OpenAI vaizdo API)",
    "Custom prompt for OpenAI Vision processing. Leave empty to use default prompt.":
      "Kustomas užklausas OpenAI vaizdo apdorojimui. Palikite tuščią, kad naudoti numatytą užklausą.",

    // Dual-Pass LLM Settings
    "Dual-Pass LLM Settings": "Dvigubas LLM apdorojimas",
    "Enhanced accuracy with two-stage processing": "Pagerintas tikslumas su dvigubu apdorojimu",
    "Enable Dual-Pass Processing": "Įjungti dvigubą apdorojimą",
    "No (Use Single Model)": "Ne (naudoti vieną modelį)",
    "Yes (Use Dual-Pass)": "Taip (naudoti dvigubą apdorojimą)",
    "Primary Model (Pass 1)": "Pagrindinis modelis (1 etapas)",
    "Primary Model Version": "Pagrindinio modelio versija",
    "Validation Model (Pass 2)": "Patikrinimo modelis (2 etapas)",
    "Validation Model Version": "Patikrinimo modelio versija",
    "Validation Custom Prompt": "Pasirinktinė patikrinimo užklausa",
    "Custom prompt for validation pass. Leave empty to use default validation prompt.":
      "Pasirinktinė užklausa patikrinimo etapui. Palikite tuščią numatytai užklausai.",

    // Authentication
    signIn: "Prisijungti",
    signUp: "Registruotis",
    email: "El. paštas",
    password: "Slaptažodis",
    confirmPassword: "Patvirtinti slaptažodį",
    firstName: "Vardas",
    lastName: "Pavardė",
    forgotPassword: "Pamiršote slaptažodį?",
    resetPassword: "Atstatyti slaptažodį",

    // Table Headers
    title: "Pavadinimas",
    uploadedBy: "Įkėlė",
    createdAt: "Sukurta",
    recognized: "Atpažinta",
    rec: "Atp",
    valid: "Galioja",
    actions: "Veiksmai",
    showAll: "Rodyti visas",
    assign: "Priskirti",
    invoiceDate: "Sąskaitos data",
    taxAmount: "Mokesčių suma",
    supplierName: "Tiekėjo pavadinimas",
    supplierRegistration: "Tiekėjo įmonės kodas",
    receiverName: "Gavėjo pavadinimas",
    receiverRegistration: "Gavėjo įmonės kodas",
    description: "Aprašymas",

    // Pagination
    docsPerPage: "Dok. per puslapį",
    page: "Puslapis",
    of: "iš",
    perPage: "Per puslapį",

    // Language
    language: "Kalba",
    english: "Anglų",
    lithuanian: "Lietuvių",

    // Dialogs and Modals
    deleteConfirmation: "Ištrynimo patvirtinimas",
    areYouSure: "Ar tikrai norite ištrinti",
    thisActionCannotBeUndone: "Šis veiksmas negali būti atšauktas.",
    refreshList: "Atnaujinti sąrašą",
    backToDocuments: "Grįžti į dokumentus",

    // XML Export Settings
    xmlExportSettings: "XML eksportavimo nustatymai",
    selectXmlExportSettings: "Pasirinkite XML eksportavimo parinktis ir požymius",
    exportType: "Eksporto tipas",
    purchases: "Pirkimai",
    salesInvoices: "Pardavimai",
    summary: "Suminis",
    recalcVatTitle: "Priskaičiuoti PVM (21%) jei dokumente PVM = 0",
    recalcVatDescription:
      "Įjungus, eksportas automatiškai paskaičiuos 21% PVM dokumentams, kuriuose PVM suma yra 0.",
    includePvmInTotal: "Įtraukti PVM į bendrą sumą (M4)",
    spacyModelSize: "spaCy modelio dydis",
    mediumModel: "Vidutinis modelis (md) - Greitesnis",
    largeModel: "Didelis modelis (lg) - Tikslesnis",
    spacyModelDescription:
      "Vidutinis modelis yra greitesnis, bet mažiau tikslus. Didelis modelis yra lėtesnis, bet tikslesnis teksto atpažinimui.",
    pvmCodeOverride: "PVM kodo perrašymas",
    selectPvmCode: "Pasirinkite PVM kodą",
    useSupplierPvmCode: "Naudoti tiekėjo PVM kodą (numatytasis)",
    pvmCodeOverrideDescription:
      "Jei pasirinkta, šis PVM kodas perrašys tiekėjo PVM kodą visiems eksportuojamiems dokumentams.",

    // Additional missing translations
    exportWithMatches: "Eksportuoti su atitikimais",
    importSuppliers: "Importuoti tiekėjus",
    savedVariants: "Išsaugoti variantus",
    // Search bar
    searchIn: "Ieškoti pagal",
    allFields: "Visi laukai",
    documentTitle: "Dokumento pavadinimas",
    exactMatch: "Tikslus atitikmuo",
    searchingIn: "Ieškoma pagal",
    contains: "turi",
    zoomOut: "Sumažinti",
    zoomIn: "Padidinti",
    previousPage: "Ankstesnis puslapis",
    nextPage: "Kitas puslapis",
    previousDocument: "Ankstesnis dokumentas",
    nextDocument: "Kitas dokumentas",
    recognizeDocument: "Atpažinti dokumentą",
    showFieldMarkers: "Rodyti laukų žymėjimus",
    hideFieldMarkers: "Slėpti laukų žymėjimus",
    fieldMarkers: "Laukų žymėjimai",
    noFieldMarkersOnPage: "Šiame puslapyje laukų žymėjimų nėra",
    fieldMarkersHidden: "Laukų žymėjimai paslėpti",
    deleteDocument: "Ištrinti dokumentą",
    downloadDocument: "Atsisiųsti dokumentą",
    printDocument: "Spausdinti dokumentą",
    documentFileUrlMissing: "Dokumento failo URL trūksta.",

    // User Management
    userManagement: "Vartotojų valdymas",
    addUser: "Pridėti vartotoją",
    filterByCompany: "Filtruoti pagal įmonę",
    assignToCompany: "Priskirti įmonei",
    setPassword: "Nustatyti slaptažodį",
    newPassword: "Naujas slaptažodis",
    confirmPassword: "Patvirtinti slaptažodį",
    confirmPasswordLabel: "Patvirtinkite slaptažodį",
    passwordsMustMatch: "Slaptažodžiai turi sutapti",
    role: "Rolė",
    active: "Aktyvus",
    inactive: "Neaktyvus",
    admin: "Administratorius",
    user: "Vartotojas",
    guest: "Svečias",
    noAccess: "Prieiga negalima",

    // Warehouse & Suppliers additions
    addItem: "Pridėti prekę",
    addWarehouseItem: "Pridėti sandėlio prekę",
    editWarehouseItem: "Redaguoti sandėlio prekę",
    itemName: "Prekės pavadinimas",
    itemGroup: "Prekės grupė",
    confirmDeleteItem: "Ar tikrai norite ištrinti šią prekę?",
    warehouseItemDeletedSuccessfully: "Prekė sėkmingai ištrinta.",
    warehouseItemsUploadedSuccessfully: "Prekės įkeltos sėkmingai!",
    supplierListRefreshed: "Tiekėjų sąrašas atnaujintas!",
    warehouseListRefreshed: "Sandėlio sąrašas atnaujintas!",
    suppliersImportedSuccessfully: "Tiekėjai sėkmingai importuoti!",
    importCompleted: "Importavimas baigtas.",
    createdSuppliers: "Sukurti tiekėjai",
    createdCount: "Sukurta: {count}",
    noCreatedSuppliers: "Naujų tiekėjų nesukurta.",
    filesUploadedSuccessfully: "Failai sėkmingai įkelti",
    selectAtLeastOneFile: "Pasirinkite bent vieną failą įkėlimui.",
    failedToUploadWarehouseItems: "Nepavyko įkelti sandėlio prekių.",
    unitPriceOptional: "Neprivaloma. Įveskite vieneto kainą ataskaitai (galite palikti tuščią).",
    deleteSelectedConfirm:
      "Ar tikrai norite ištrinti {count} pasirinktą(-us) dokumentą(-us)? Šio veiksmo atšaukti negalima.",
    perPage: "Per puslapį",
    errorFetchingWarehouseItems: "Klaida gaunant sandėlio prekes.",
    errorDeletingWarehouseItem: "Klaida trinant sandėlio prekę.",
    warehouseItemUpdatedSuccessfully: "Prekė sėkmingai atnaujinta!",
    warehouseItemCreatedSuccessfully: "Prekė sėkmingai sukurta!",
    unknownErrorOccurred: "Įvyko nežinoma klaida",
    failedToSaveItem: "Nepavyko išsaugoti prekės.",
    // Document viewer actions
    confirmDeleteDocument: "Ar tikrai norite ištrinti šį dokumentą?",
    documentDeletedSuccessfully: "Dokumentas sėkmingai ištrintas",
    errorDeletingDocument: "Klaida trinant dokumentą",
    notesSavedSuccessfully: "Pastabos sėkmingai išsaugotos",
    errorSavingNotes: "Klaida išsaugant pastabas",
    informationSavedSuccessfully: "Informacija sėkmingai išsaugota",
    errorSavingInformation: "Klaida išsaugant informaciją",
    documentUpdatedSuccessfully: "Dokumentas sėkmingai atnaujintas",
    failedToUpdateDocument: "Nepavyko atnaujinti dokumento",
    lineItemsSavedSuccessfully: "Eilutės sėkmingai išsaugotos",
    errorSavingLineItems: "Klaida išsaugant eilutes",
    documentRecognizedSuccessfully: "Dokumentas sėkmingai atpažintas",
    errorRecognizingDocument: "Klaida atpažįstant dokumentą",
    gatewayTimeoutSuffix: ": serverio laiko limitas (504)",
    filesUploadedSuccessfully: "Dokumentai sėkmingai įkelti.",
    failedToUploadDocuments: "Nepavyko įkelti dokumentų",
    errorUploadingFiles: "Klaida įkeliant dokumentus",
    // Server summary patterns
    uploadSummary: "Sėkmingai įkelta {uploaded} dokumentų. Nepavyko įkelti {failed} dokumentų.",
    documentsDeletedCount: "Ištrinta {count} dokumentų.",
    // Recognition progress
    processingDocumentProgress: "Apdorojamas dokumentas {i}/{n} (ID {id})...",
    processingBatchStart: "Pradedamas {n} dokumentų atpažinimas...",
    recognitionSummary: "Atpažinimas baigtas. Sėkmingai: {ok}, Nesėkmingai: {fail}.",
    recognitionFailedFor: "Dokumento {id} atpažinimas nepavyko",
    notRecognized: "Neatpažintas",
    unknownSupplier: "Nežinomas tiekėjas",
    unitPrice: "Vieneto kaina",
    processing: "Apdorojama...",
    cancelRecognition: "Atšaukti atpažinimą",
    cancelling: "Atšaukiama...",
    recognitionInProgress: "Vykdomas atpažinimas",
    recognitionCancelled: "Atpažinimas atšauktas",
    recognitionFinished: "Atpažinimas baigtas",
    documentsProcessed: "Apdoroti dokumentai",
    recognitionWasCancelled: "Paketuų apdorojimas atšauktas.",
    recognitionAllSuccess: "Visi {ok} dokumentų sėkmingai atpažinti!",
    recognitionCancelRequested: "Atpažinimo atšaukimas prašytas",
    recognitionCancelFailed: "Nepavyko prašyti atšaukimo",
    // XML eksportas fone
    xmlExportInProgress: "XML eksportas vykdomas",
    xmlExportFailed: "XML eksportas nepavyko",
    xmlExportReady: "XML paruoštas atsisiuntimui",
    downloadXml: "Atsisiųsti XML",
    // Generic notifications
    savedSuccessfully: "Sėkmingai išsaugota",
    updatedSuccessfully: "Sėkmingai atnaujinta",
    errorOccurred: "Įvyko klaida",
    // Document Type Labels
    documentType: "Dokumento tipas",
    purchaseDocument: "Pirkimo dokumentas",
    salesDocument: "Pardavimo dokumentas",
    returnDocument: "Grąžinimo / kreditinis dokumentas",
    otherDocument: "Kitas dokumentas",
    unknownDocumentType: "Nežinomas dokumento tipas",

    // Product Matching LLM Settings
    "Product Matching LLM Settings": "Prekių susiejimo LLM nustatymai",
    "Product Matching Model": "Prekių susiejimo modelis",
    "Product Matching Model Version": "Prekių susiejimo modelio versija",
    matcherType: "Susiejimo metodas",
    spacy: "spaCy / Fuzzy",
    llm: "LLM (AI)",

    // Atpažinimo būsenos filtras
    recognitionStatus: "Atpažinimo būsena",
    unrecognized: "Neatpažinti",

    // Vartotojų valdymas
    selectAllCompanies: "Pasirinkti visas įmones",
  },
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    return localStorage.getItem("language") || "en";
  });

  const changeLanguage = (language) => {
    setCurrentLanguage(language);
    localStorage.setItem("language", language);
  };

  const t = (key) => {
    return translations[currentLanguage]?.[key] || key;
  };

  useEffect(() => {
    // Save language preference whenever it changes
    localStorage.setItem("language", currentLanguage);
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    translations: translations[currentLanguage],
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LanguageContext;
